export interface Balance {
  /**
   * Balance ID. This is the same as the associated pool's ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  id?: string;
  /**
   * Revision number, which increments by 1 each time the balance is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the balance.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the balance was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the balance was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Beneficiary of the associated pool.
   * @readonly
   * @immutable
   */
  beneficiary?: CommonIdentificationData;
  /**
   * Available credits.
   * @readonly
   */
  amount?: BalanceAmount;
  /**
   * Associated pool information.
   * @readonly
   */
  poolInfo?: PoolInfo;
  /**
   * Custom field data for the balance object.
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * ID of the transaction associated with the most recent change to the balance.
   * @format GUID
   * @readonly
   */
  lastTransactionId?: string | null;
}

export interface CommonIdentificationData
  extends CommonIdentificationDataIdOneOf {
  /**
   * ID of a site visitor that hasn't logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site member.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user.
   * @format GUID
   */
  wixUserId?: string;
}

/** @oneof */
export interface CommonIdentificationDataIdOneOf {
  /**
   * ID of a site visitor that hasn't logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site member.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user.
   * @format GUID
   */
  wixUserId?: string;
}

export enum IdentityType {
  /** Unknown type. This value is not used. */
  UNKNOWN = 'UNKNOWN',
  /** A site visitor who has not logged in. */
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  /** A logged-in site member. */
  MEMBER = 'MEMBER',
  /** A Wix account holder, such as a site owner or contributor. */
  WIX_USER = 'WIX_USER',
}

/** @enumType */
export type IdentityTypeWithLiterals =
  | IdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER';

export interface BalanceAmount {
  /**
   * number of available credits.
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  available?: string;
  /**
   * number of reserved credits.
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  reserved?: string;
}

export interface PoolInfo {
  /**
   * Pool ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * ID of the pool definition the pool was created from.
   * @format GUID
   * @readonly
   */
  poolDefinitionId?: string | null;
  /**
   * ID of the program definition the pool was created from.
   * @format GUID
   * @readonly
   */
  programDefinitionId?: string | null;
  /**
   * ID of the program that contains the pool.
   * @format GUID
   * @readonly
   */
  programId?: string | null;
  /**
   * Available credits.
   * @decimalValue options { gte:0, maxScale:4 }
   * @readonly
   */
  creditAmount?: string | null;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @minLength 1
   * @maxLength 20
   * @readonly
   */
  namespace?: string | null;
  /**
   * External ID of the program definition the pool was created from.
   * @format GUID
   * @readonly
   */
  externalProgramDefinitionId?: string | null;
  /**
   * External ID of the program that contains the pool.
   * @format GUID
   * @readonly
   */
  externalProgramId?: string | null;
  /**
   * Credit rollover configuration information for the pool.
   * @readonly
   */
  creditRolloverConfiguration?: RolloverConfigurationInfo;
  /**
   * Pool status
   * @readonly
   */
  status?: PoolStatusWithLiterals;
}

export interface RolloverConfigurationInfo {
  /**
   * Whether unused credits roll over to a new cycle when a program renews.
   * @readonly
   */
  enabled?: boolean | null;
  /**
   * Maximum number of credits that can roll over to the next cycle when a program renews.
   * @decimalValue options { gte:0, maxScale:4 }
   * @readonly
   */
  balanceCap?: string | null;
}

export enum PoolStatus {
  /** Pool is active. */
  ACTIVE = 'ACTIVE',
  /** Pool is paused. It can be resumed. */
  PAUSED = 'PAUSED',
  /** Pool is ended. It can't be resumed. */
  ENDED = 'ENDED',
  /** Pool provisioning is in progress. This status exists for a short time during processing. */
  PROVISIONING = 'PROVISIONING',
  /** Pool renewing is in progress. This status exists for a short time during processing. */
  RENEWING = 'RENEWING',
  /** Pool is pending */
  PENDING = 'PENDING',
}

/** @enumType */
export type PoolStatusWithLiterals =
  | PoolStatus
  | 'ACTIVE'
  | 'PAUSED'
  | 'ENDED'
  | 'PROVISIONING'
  | 'RENEWING'
  | 'PENDING';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface GetBalanceRequest {
  /**
   * ID of the pool associated with the balance to retrieve. This is also the ID of the balance.
   * @format GUID
   */
  poolId: string;
}

export interface GetBalanceResponse {
  /** Retrieved balance. */
  balance?: Balance;
}

export interface ListBalancesRequest {
  /**
   * List of IDs of the pools associated with the balances to retrieve. These are also the IDs of the balances.
   * @format GUID
   * @maxSize 100
   */
  poolIds?: string[];
  /** Cursor paging. */
  cursorPaging?: CursorPaging;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ListBalancesResponse {
  /**
   * Retrieved balances.
   * @maxSize 100
   */
  balances?: Balance[];
  /** Metadata for paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface QueryBalancesRequest {
  /** Filter, sort, and paging to apply to the query. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /**
   * Sort order. Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`
   */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Ascending sort order. */
  ASC = 'ASC',
  /** Descending sort order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryBalancesResponse {
  /** List of retrieved balances. */
  balances?: Balance[];
  /** Metadata for the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface BalanceChangeBalanceRequest
  extends BalanceChangeBalanceRequestOperationOneOf {
  /** Adjust the balance by a specific number of credits. */
  adjustOptions?: AdjustOptions;
  /** Set the balance to a specific number of credits. */
  setOptions?: SetOptions;
  /**
   * ID of the pool associated with the balance to change. This is also the ID of the balance.
   * @format GUID
   */
  poolId: string;
  /**
   * Unique identifier, generated by the client.
   * Used to recognize repeated attempts to make the same request.
   * @maxLength 200
   */
  idempotencyKey: string;
  /** Identity changing the balance. */
  instructingParty?: CommonIdentificationData;
  /** Balance change type. */
  type?: ChangeBalanceRequestTypeWithLiterals;
  /** Details to send to the transaction created from this balance change. */
  transactionDetails?: TransactionDetails;
}

/** @oneof */
export interface BalanceChangeBalanceRequestOperationOneOf {
  /** Adjust the balance by a specific number of credits. */
  adjustOptions?: AdjustOptions;
  /** Set the balance to a specific number of credits. */
  setOptions?: SetOptions;
}

export enum ChangeBalanceRequestType {
  /** Unknown balance change type. */
  UNKNOWN_OPERATION = 'UNKNOWN_OPERATION',
  /** Use with `adjustOptions`. */
  ADJUST = 'ADJUST',
  /** Use with `setOptions`. */
  SET = 'SET',
}

/** @enumType */
export type ChangeBalanceRequestTypeWithLiterals =
  | ChangeBalanceRequestType
  | 'UNKNOWN_OPERATION'
  | 'ADJUST'
  | 'SET';

export interface AdjustOptions {
  /**
   * Amount to change the balance's available credits by.
   * - To increase the balance, specify a positive number.
   * - To decrease the balance, specify a negative number.
   * @decimalValue options { maxScale:4 }
   */
  value?: string;
  /** Beneficiary of the associated pool. */
  beneficiary?: CommonIdentificationData;
}

export enum LimitRule {
  /** If the limit is exceeded, the operation will fail */
  FAIL = 'FAIL',
  /** If the limit is exceeded, the operation will succeed but the balance never goes beyond the provided limits. For balance that was already outside of provided limits, it will not be changed. */
  SOFT_ENFORCE = 'SOFT_ENFORCE',
  /** If the limit is exceeded, the operation will succeed but the balance will be adjusted so that it satisfies the provided limits. If the resulting balance is below the lower_limit, it will be set to the lower_limit. If the resulting balance is above the upper_limit, it will be set to the upper_limit. */
  HARD_ENFORCE = 'HARD_ENFORCE',
}

/** @enumType */
export type LimitRuleWithLiterals =
  | LimitRule
  | 'FAIL'
  | 'SOFT_ENFORCE'
  | 'HARD_ENFORCE';

export enum BalanceType {
  /** In a pool's balance. */
  AVAILABLE = 'AVAILABLE',
  /** Outside a pool's balance. */
  EXTERNAL = 'EXTERNAL',
}

/** @enumType */
export type BalanceTypeWithLiterals = BalanceType | 'AVAILABLE' | 'EXTERNAL';

export interface SetOptions {
  /**
   * Amount to set the balance's available credits to.
   *
   * If this is the same as the current value, no transaction is created.
   * @decimalValue options { maxScale:4 }
   */
  value?: string;
  /** Beneficiary of the associated pool. */
  beneficiary?: CommonIdentificationData;
}

export interface SetInitialOptions {
  /**
   * Set the available balance to the provided amount.
   * @decimalValue options { maxScale:4 }
   */
  value?: string;
  /** Benefit pool owner associated with this balance. */
  beneficiary?: CommonIdentificationData;
}

export interface TransactionDetails {
  /**
   * Item associated with the transaction.
   * @readonly
   */
  item?: Item;
  /** Amount of items associated with the transaction. */
  itemCount?: number | null;
  /**
   * Date and time the transaction was created.
   * @readonly
   */
  effectiveDate?: Date | null;
  /**
   * Reason for the transaction. For example, `Redemption`.
   * @readonly
   * @maxLength 256
   */
  reason?: string | null;
  /**
   * Benefit key associated with the transaction.
   * @maxLength 64
   * @immutable
   */
  benefitKey?: string | null;
}

export interface Item {
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Item external ID.
   * @format GUID
   * @readonly
   */
  externalId?: string | null;
  /**
   * Item category.
   * @maxLength 20
   * @readonly
   */
  category?: string | null;
  /**
   * Item set ID.
   * @format GUID
   * @readonly
   */
  itemSetId?: string | null;
  /**
   * Item name.
   * @maxLength 64
   * @readonly
   */
  displayName?: string | null;
  /**
   * Provider app id
   * @format GUID
   * @readonly
   */
  providerAppId?: string | null;
}

export interface ChangeBalanceResponse {
  /** Updated balance. */
  balance?: Balance;
  /**
   * ID of the transaction associated with this change in balance.
   * @format GUID
   */
  transactionId?: string | null;
}

export interface BalanceExceededLimits {
  /** Current balance */
  balance?: BalanceAmount;
  /**
   * The requested amount
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  requested?: string;
  /**
   * Lower limit
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  lowerLimit?: string | null;
  /**
   * Upper limit
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  upperLimit?: string | null;
  /** Source balance type */
  sourceBalanceType?: BalanceTypeWithLiterals;
  /** Target balance type */
  targetBalanceType?: BalanceTypeWithLiterals;
}

export interface BulkChangeBalancesRequest {
  /**
   * List of balances to change.
   * @minSize 1
   * @maxSize 100
   */
  requests?: ChangeBalanceRequest[];
  /**
   * Unique identifier, generated by the client.
   * Used to recognize repeated attempts to make the same request.
   * @maxLength 200
   */
  idempotencyKey?: string;
  /** Whether to return the full balance entities in the response. */
  returnEntity?: boolean;
}

export enum Type {
  /** Unknown balance change type. */
  UNKNOWN_OPERATION = 'UNKNOWN_OPERATION',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNKNOWN_OPERATION';

export interface ChangeBalanceRequest
  extends ChangeBalanceRequestOperationOneOf {
  /**
   * ID of the pool associated with the balance to change. This is also the ID of the balance.
   * @format GUID
   */
  poolId?: string;
  /** Identity changing the balance. */
  instructingParty?: CommonIdentificationData;
  /** Balance change type. */
  type?: TypeWithLiterals;
  /** Details to send to the transaction created from this balance change. */
  transactionDetails?: TransactionDetails;
}

/** @oneof */
export interface ChangeBalanceRequestOperationOneOf {}

export interface BulkChangeBalancesResponse {
  /**
   * Updated item and associated metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkChangeBalanceResult[];
  /** Metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkChangeBalanceResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Updated item. */
  balance?: Balance;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface RevertBalanceChangeRequest {
  /**
   * ID of the transaction associated with the balance change to revert.
   * @format GUID
   */
  transactionId: string;
  /**
   * Ignored value, kept for backward compatibility. Idempotency is now handled by the system.
   * @maxLength 200
   * @deprecated Ignored value, kept for backward compatibility. Idempotency is now handled by the system.
   * @targetRemovalDate 2026-01-31
   */
  idempotencyKey: string;
  /** Identity reverting the balance change. */
  instructingParty?: CommonIdentificationData;
}

export interface RevertBalanceChangeResponse {
  /**
   * ID of the transaction associated with the balance reversion.
   * @format GUID
   */
  transactionId?: string | null;
}

export interface ChangeAlreadyReverted {
  /**
   * The id of the transaction which was already reverted
   * @format GUID
   */
  originalTransactionId?: string;
  /**
   * The id of the transaction which reverted the original transaction
   * @format GUID
   */
  revertedTransactionId?: string;
}

export interface NotEnoughBalance {
  /** Current balance */
  balance?: BalanceAmount;
  /**
   * The requested amount
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  requested?: string;
}

export interface GetTransactionReversibilityRequest {
  /**
   * Id of the transaction to get the reversibility
   * @format GUID
   */
  transactionId?: string;
}

export interface GetTransactionReversibilityResponse {
  /** The result of transaction reversibility validation */
  transactionReversibility?: TransactionReversibilityWithLiterals;
}

/** Transaction reversibility results */
export enum TransactionReversibility {
  /** Transaction is allowed to be reverted */
  TRANSACTION_IS_REVERSIBLE = 'TRANSACTION_IS_REVERSIBLE',
  /** Transaction isn't allowed to be reverted, because it was already reverted */
  TRANSACTION_ALREADY_REVERSED = 'TRANSACTION_ALREADY_REVERSED',
}

/** @enumType */
export type TransactionReversibilityWithLiterals =
  | TransactionReversibility
  | 'TRANSACTION_IS_REVERSIBLE'
  | 'TRANSACTION_ALREADY_REVERSED';

export interface HealBalanceRequest {
  /**
   * ID of the balance whose enrichments should be re-materialized. This is also the ID of the pool.
   * @format GUID
   */
  balanceId?: string;
}

export interface HealBalanceResponse {}

export interface HealBalanceTenantRequest {}

export interface HealBalanceTenantResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type ChangeBalanceApplicationErrors =
  | {
      code?: 'ALREADY_EXECUTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'BALANCE_ALREADY_INITIALIZED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'BALANCE_EXCEEDED_LIMITS';
      description?: string;
      data?: BalanceExceededLimits;
    };
/** @docsIgnore */
export type RevertBalanceChangeApplicationErrors =
  | {
      code?: 'CHANGE_ALREADY_REVERTED';
      description?: string;
      data?: ChangeAlreadyReverted;
    }
  | {
      code?: 'NOT_ENOUGH_BALANCE';
      description?: string;
      data?: NotEnoughBalance;
    }
  | {
      code?: 'TRANSACTION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
